import { decodeBriefPlainText } from "utils/utils";
import { API } from "service/Interceptor";

const STORAGE_KEY = "qreativs.creatorBriefs.v1";

/** Internal marker for structured creator collab posts (never shown in UI). */
export const CREATOR_BRIEF_MARKER = "#openbrief";

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
};

export const getCreatorBriefs = (userKey) => {
  if (!userKey) return [];
  const all = readAll();
  const list = all[String(userKey)];
  return Array.isArray(list) ? list : [];
};

export const upsertCreatorBrief = (userKey, brief) => {
  if (!userKey || !brief?.jobId) return getCreatorBriefs(userKey);
  const all = readAll();
  const key = String(userKey);
  const prev = Array.isArray(all[key]) ? all[key] : [];
  const without = prev.filter((b) => b?.jobId !== brief.jobId);
  const next = [brief, ...without];
  all[key] = next;
  writeAll(all);
  return next;
};

export const removeCreatorBrief = (userKey, jobId) => {
  if (!userKey || !jobId) return getCreatorBriefs(userKey);
  const all = readAll();
  const key = String(userKey);
  const prev = Array.isArray(all[key]) ? all[key] : [];
  const next = prev.filter((b) => b?.jobId !== jobId);
  all[key] = next;
  writeAll(all);
  removeCreatorBriefApplicants(jobId);
  return next;
};

const APPLICANTS_KEY = "qreativs.creatorBriefApplicants.v1";

const readApplicants = () => {
  try {
    const raw = localStorage.getItem(APPLICANTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeApplicants = (data) => {
  try {
    localStorage.setItem(APPLICANTS_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
};

export const getCreatorBriefApplicants = (jobId) => {
  if (!jobId) return [];
  const all = readApplicants();
  const list = all[String(jobId)];
  return Array.isArray(list) ? list : [];
};

export const removeCreatorBriefApplicants = (jobId) => {
  if (!jobId) return;
  const all = readApplicants();
  delete all[String(jobId)];
  writeApplicants(all);
};

export const addCreatorBriefApplicant = (jobId, applicant) => {
  if (!jobId || !applicant) return [];
  const all = readApplicants();
  const key = String(jobId);
  const prev = Array.isArray(all[key]) ? all[key] : [];
  const id =
    applicant.id ||
    applicant.userId ||
    `app-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const entry = {
    ...applicant,
    id,
    appliedAt: applicant.appliedAt || new Date().toISOString(),
  };
  const without = prev.filter(
    (a) => a.id !== id && a.userId !== applicant.userId
  );
  const next = [entry, ...without];
  all[key] = next;
  writeApplicants(all);
  return next;
};

export const setCreatorBriefApplicantStatus = (jobId, applicantKey, status) => {
  if (!jobId || !applicantKey) return getCreatorBriefApplicants(jobId);
  const all = readApplicants();
  const key = String(jobId);
  const prev = Array.isArray(all[key]) ? all[key] : [];
  const needle = String(applicantKey);
  let found = false;
  const next = prev.map((a) => {
    if (
      String(a.id) === needle ||
      String(a.userId) === needle ||
      String(a.applicationNumber) === needle
    ) {
      found = true;
      return { ...a, status, applicationStatus: status };
    }
    return a;
  });
  if (!found) {
    next.push({
      id: needle,
      userId: needle,
      applicationStatus: status,
      status,
      appliedAt: new Date().toISOString(),
    });
  }
  all[key] = next;
  writeApplicants(all);
  return next;
};

export const isApplyComment = (message = "") => {
  const text = String(message || "");
  return (
    /i'?d like to collaborate on this brief/i.test(text) ||
    /^Work:\s*\S+/im.test(text) ||
    /^Portfolio:\s*\S+/im.test(text)
  );
};

export const applicantIdentityKeys = (a = {}) => {
  const keys = [];
  const email = String(a.email || "").trim().toLowerCase();
  if (email.includes("@")) keys.push(`e:${email}`);
  const userId = a.userId;
  if (userId != null && /^\d+$/.test(String(userId))) keys.push(`u:${userId}`);
  const alias = String(a.userAliasId || "").trim().toLowerCase();
  if (alias) keys.push(`a:${alias}`);
  return keys;
};

/** Parse Apply dialog comment body into note / work links / portfolio. */
export const parseApplyComment = (message = "") => {
  const text = String(message || "");
  const note =
    text.match(/Note:\s*([^\n]+)/i)?.[1]?.trim() ||
    (text
      .split("\n")
      .map((l) => l.trim())
      .find(
        (l) =>
          l &&
          !/^i'?d like to collaborate/i.test(l) &&
          !/^(note|work|portfolio):/i.test(l)
      ) || null);
  const workRaw = text.match(/Work:\s*([^\n]+)/i)?.[1]?.trim() || "";
  const workLinks = workRaw
    ? workRaw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
    : [];
  const portfolioUrl =
    text.match(/Portfolio:\s*([^\n]+)/i)?.[1]?.trim() || null;
  return { note, workLinks, portfolioUrl };
};

export const buildCreatorBriefPostContent = ({
  need,
  industry,
  creativeType,
  workStyle,
  city,
  budgetValue,
  timeline,
  projectStartDate,
  projectEndDate,
  jobId,
}) =>
  [
    (need || "").trim(),
    `Looking for: ${creativeType || "Creative"}`,
    `Work style: ${workStyle || "—"}`,
    city ? `City: ${city}` : null,
    budgetValue != null && budgetValue !== ""
      ? `Budget: ${budgetValue}`
      : null,
    `Timeline: ${timeline || "—"}`,
    projectStartDate && projectEndDate
      ? `Project dates: ${projectStartDate} → ${projectEndDate}`
      : null,
    industry ? `Industry: ${industry}` : null,
    jobId ? `jobId: ${jobId}` : null,
    // Marker kept for detection; UI strips it before display.
    CREATOR_BRIEF_MARKER,
  ]
    .filter((line) => line != null)
    .join("\n");

const fieldFromContent = (content, label) => {
  const re = new RegExp(`${label}:\\s*([^\\n]+)`, "i");
  return decodeBriefPlainText(content).match(re)?.[1]?.trim() || null;
};

/** Structured open-brief posts (creator collabs + brand job announcements). */
export const isCreatorBriefPost = (post) => {
  if (!post) return false;
  const tags = Array.isArray(post.tags) ? post.tags : [];
  const content = String(post.content || "");
  if (!tags.map(String).includes("BRIEFS")) return false;
  return (
    content.includes(CREATOR_BRIEF_MARKER) ||
    /Looking for:\s*/i.test(content)
  );
};

const JOB_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Applicants list for a brief. Prefer the real Job UUID when the post embeds one. */
export const briefApplicantsPath = (project) => {
  const postId = project?.postId;
  const jobId = project?.jobId;
  if (jobId && JOB_UUID_RE.test(String(jobId))) {
    return `/projects/${jobId}/applicants`;
  }
  if (String(jobId || "").startsWith("post-")) {
    return `/projects/${jobId}/applicants`;
  }
  if (postId) return `/projects/post-${postId}/applicants`;
  if (jobId) return `/projects/${jobId}/applicants`;
  return "/projects";
};

export const mapPostToOpenBrief = (post) => {
  if (!post?.id) return null;
  const content = decodeBriefPlainText(post.content || "");
  const owner = post.postOwner || {};
  const isBusiness =
    owner?.userType === "BUSINESS" || !!owner?.companyName;
  const name = isBusiness
    ? owner?.companyName ||
      `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim()
    : `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
      "Creator";
  const alias = post.userAliasId || owner?.userAliasId || null;
  const profileUrl = owner?.profileUrl || null;
  const looking = fieldFromContent(content, "Looking for") || "Creative";
  const workStyle = fieldFromContent(content, "Work style");
  const cityRaw =
    fieldFromContent(content, "City") ||
    owner?.cityName ||
    "Remote";
  // Keep city chip clean — strip trailing ", State" if present
  const city = String(cityRaw).split(",")[0].trim() || cityRaw;
  const budgetRaw = fieldFromContent(content, "Budget");
  const budgetNum = budgetRaw != null ? Number(budgetRaw) : null;
  const timeline = fieldFromContent(content, "Timeline");
  const industry = fieldFromContent(content, "Industry");
  const linkedJobId = fieldFromContent(content, "jobId");
  const needLine =
    content
      .split("\n")
      .map((l) => l.trim())
      .find(
        (l) =>
          l &&
          l !== CREATOR_BRIEF_MARKER &&
          !/^(looking for|work style|city|budget|timeline|project dates|industry|jobid|applicationsclosed):/i.test(
            l
          )
      ) || "Open brief";

  // Creator-owned collab (even when linked to a real Job UUID for Apply).
  const isCreatorBrief = owner?.userType !== "BUSINESS" && owner?.userType !== 1;

  return {
    jobId: linkedJobId || `post-${post.id}`,
    postId: post.id,
    jobTitle: needLine.slice(0, 80),
    description: content,
    skillsRequired: [looking].filter(Boolean),
    industry: industry ? [industry] : [],
    category: industry ? { name: industry } : null,
    cityName: city,
    stateName: owner?.stateName || "",
    projectBudget:
      budgetNum != null && !Number.isNaN(budgetNum) ? budgetNum : null,
    projectDuration: timeline || null,
    noOfOpenings: 1,
    workStyle: workStyle || null,
    createdAt: post.createdAt || new Date().toISOString(),
    applicantCount: post.commentCount ?? 0,
    isCreatorBrief,
    applicationsClosed: /applicationsClosed:\s*true/i.test(content),
    _local: false,
    companyJobRespDto: {
      name,
      logo: profileUrl,
      userAliasId: alias,
      companyAliasId: alias,
    },
    company: {
      name,
      logo: profileUrl,
      userAliasId: alias,
    },
  };
};

export const buildCreatorBriefSnapshot = ({
  need,
  industry,
  creativeType,
  workStyle,
  city,
  budgetValue,
  timeline,
  projectStartDate,
  projectEndDate,
  userProfile,
  serverJob,
  postId,
  jobId: realJobId,
}) => {
  const name =
    `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim() ||
    userProfile?.name ||
    "Creator";
  const alias =
    userProfile?.userAliasId || userProfile?.aliasId || null;
  const profileUrl = userProfile?.profileUrl || null;
  const resolvedPostId = postId || serverJob?.postId || null;
  // Prefer real Job UUID so Apply / applicants use the jobs API.
  const jobId =
    realJobId ||
    serverJob?.jobId ||
    serverJob?.id ||
    (resolvedPostId != null ? `post-${resolvedPostId}` : null) ||
    `creator-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return {
    jobId,
    postId: resolvedPostId,
    jobTitle: (need || "").trim().slice(0, 80),
    description: buildCreatorBriefPostContent({
      need,
      industry,
      creativeType,
      workStyle,
      city,
      budgetValue,
      timeline,
      projectStartDate,
      projectEndDate,
      jobId,
    }),
    skillsRequired: [creativeType || "Creative"].filter(Boolean),
    industry: industry ? [industry] : [],
    category: industry ? { name: industry } : null,
    cityName: city || userProfile?.cityName || "Remote",
    stateName: userProfile?.stateName || "",
    projectBudget: budgetValue ?? null,
    projectDuration: timeline || null,
    noOfOpenings: 1,
    workStyle: workStyle || null,
    createdAt: serverJob?.createdAt || new Date().toISOString(),
    applicantCount: serverJob?.applicantCount ?? 0,
    isCreatorBrief: true,
    _local: !realJobId && !resolvedPostId,
    companyJobRespDto: {
      name,
      logo: profileUrl,
      userAliasId: alias,
      companyAliasId: alias,
    },
    company: {
      name,
      logo: profileUrl,
      userAliasId: alias,
    },
  };
};

export const isApplicationsClosed = (project) => {
  if (!project) return false;
  if (project.applicationsClosed === true) return true;
  const text = String(project.description || project.content || "");
  return /applicationsClosed:\s*true/i.test(text);
};

export const withApplicationsClosedMarker = (content, closed) => {
  const stripped = String(content || "")
    .replace(/^\s*applicationsClosed:\s*(true|false)\s*$/gim, "")
    .replace(/\n?applicationsClosed:\s*(true|false)/gi, "")
    .trim();
  return closed ? `${stripped}\napplicationsClosed: true` : stripped;
};

const uuidJobId = (jobId) =>
  !!jobId &&
  !String(jobId).startsWith("post-") &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(jobId)
  );

export const setBriefApplicationsClosed = async (project, closed) => {
  const postId =
    project?.postId ||
    (String(project?.jobId || "").startsWith("post-")
      ? String(project.jobId).replace(/^post-/, "")
      : null);
  if (uuidJobId(project?.jobId)) {
    await API.post(`api/jobs/${project.jobId}/close-applications`, { closed });
    return;
  }
  if (postId) {
    await API.put(`api/post/${postId}`, {
      content: withApplicationsClosedMarker(project.description, closed),
      tags: ["BRIEFS"],
    });
    return;
  }
  throw new Error("Could not update this brief");
};
